import Close from '@/components/Close';
import Grid from '@/components/Grid';
import Modal from '@/components/Modal/Modal';
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components'
import { NodeElement } from '@/api/project';
import Pagination from '@/ui/components/Pagination/Pagination';
import { useProjectStore } from '@/stores/projectStore';
import { useNodes } from '@/api/hooks/useNodes';
import {  SelectInput } from '@/ui/components/Field/Select';
import { Input } from '@/ui/components/Field/Input';
import { debounce } from 'lodash';
import FormLoader from '@/components/ui/form/FormLoader';

import useResponsive from '@/hooks/useResponsive';
import MobilePlaceItem from './MobilePlaceItem';
import { useFormikContext } from 'formik';
import { useUserStore, RolesEnum } from '@/stores/userStore';
import { translate } from '@/i18n';
import { useIntl } from 'react-intl';
import Toolbar from '@/components/Toolbar/Toolbar';

type ReportModalProps = {
    isOpen: boolean
    onClose: () => void
    setFieldValue: any
}

const SelectSeatModal: React.FC<ReportModalProps> = ({ isOpen, onClose }) => {
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [place, setPlace] = useState<any>(null)
    const [parent, setParent] = useState<string>('')
    const [name, setName] = useState<string>('')
    const intl = useIntl()

    const { values, setFieldValue } = useFormikContext<any>()
    const { isMobile } = useResponsive()

    const categories = useUserStore(state => state.categories)
    const role = useUserStore(state => state.role)

    const nodes = useProjectStore(state => state.nodes)
    const { data, isLoading } = useNodes({
        page: currentPage - 1,
        perPage: 10,
        name,
        parent,
        sort: 'name',
        direction: 0,
        bookable: 1,
        categories: role === RolesEnum.Admin ? undefined : categories.join(',')
    })

    useEffect(() => {
        if (isOpen) {
            setPlace({
                id: values.seat.id || '',
                name: values.seat.name || ''
            })
            setParent('')
            setCurrentPage(1)
            setName('')
        }
    }, [isOpen])

    const handleNameChange = (e) => setName(e?.target?.value)
    const handleParentChange = (e) => setParent(e?.target?.value)

    const debouncedNameResponse = useMemo(() => {
        return debounce(handleNameChange, 500)
    }, [])

    useEffect(() => {
        return () => debouncedNameResponse.cancel()
    }, [])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            maxWidth={620}
        >
            <Header>
                <Title>{translate('select-location')}</Title>
                <Close color="#000" onClick={onClose} />
            </Header>
            <div>
                <Grid>
                    <Filters>
                        <FiltersItem>
                            <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>{translate("levels")}</Toolbar.Label>
                            <SelectInput
                                style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                                $fullWidth
                                onChange={handleParentChange}
                                value={parent}
                            >
                                <option value="">{translate('all-levels')}</option>
                                {nodes.map(node => <option key={node.id} value={node.id}>{node.name}</option>)}
                            </SelectInput>
                        </FiltersItem>
                        <FiltersItem>
                            <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>{translate("search")}</Toolbar.Label>
                            <Input
                                style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                                $fullWidth
                                placeholder={intl.formatMessage({ id: 'floor' })}
                                onChange={debouncedNameResponse}
                                // value={name}
                            />
                        </FiltersItem>
                    </Filters>

                    {!isMobile && (
                        <Grid.RowHeader $cols="1fr 1fr 1fr">
                            <Grid.Item />
                            <Grid.Item>{translate('level')}</Grid.Item>
                            <Grid.Item>{translate('place')}</Grid.Item>
                            <Grid.Item>{translate('type')}</Grid.Item>
                        </Grid.RowHeader>
                    )}

                    {data && data.items && data?.items.length ? (
                        <>
                            {!isMobile && data.items.map((node, inx) => (
                                <PlaceItem
                                    key={node.id}
                                    place={node}
                                    index={inx}
                                    handleSelection={(selected: NodeElement) => {
                                        const placename = nodes.find(tree => tree.id == Number(selected.parent))
                                        setFieldValue('seat', {
                                            id: selected.id,
                                            name: [placename?.name || 'Не указано', selected.name].join('. ')
                                        })
                                        onClose()
                                    }}
                                />
                            ))}

                            {isMobile && data.items.map((placeData) => {
                                const placename = nodes.find(tree => tree.id == Number(placeData.parent))
                                return (
                                    <MobilePlaceItem
                                        key={placeData.id}
                                        place={placeData}
                                        selected={place}
                                        handleSelection={() => {
                                            setFieldValue('seat', {
                                                id: placeData.id,
                                                name: [placename?.name || 'Не указано', placeData.name].join('. ')
                                            })
                                            onClose()
                                        }}
                                    />
                                )
                            })}
  
                            <PaginationWrapper>
                                <Pagination currentPage={currentPage} total={data.total} itemsPerPage={10} handlePageChange={setCurrentPage} />
                            </PaginationWrapper>
                        </>
                    ) : <NotFound>{translate('no-results')}</NotFound>}

                    <FormLoader isLoading={isLoading} />
                </Grid>
            </div>
        </Modal>
    )
}

export default SelectSeatModal

const NotFound = styled.div`
    width: 100%;
    text-align: center;
    padding: 12px 0;
`

const Filters = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 8px;
    padding: 10px 0 20px 0;
`
const FiltersItem = styled.div`
`

const PlaceItem: React.FC<{ place: NodeElement; handleSelection: (place: NodeElement) => void; index: number }> = ({ place, handleSelection, index }) => {
    return (
        <StyledGridRow $cols="1fr 1fr 1fr" as="label" $label $index={index} onClick={() => handleSelection(place)}>
            <Grid.Item>{place.name}</Grid.Item>
            <Grid.Item>{place.name}</Grid.Item>
            <Grid.Item>{place.type_name}</Grid.Item>
        </StyledGridRow>
    )
}

const PaginationWrapper = styled.div`
    padding: 2rem 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const Title = styled.div`
  font-weight: 500;
  font-size: 20px;
  line-height: 2.4rem;
  color: #0A0A0A;
`

const StyledGridRow = styled(Grid.Row)<{ $index: number }>`
  background: ${({ $index }) => ($index % 2 === 0 ? '#F2F3F4' : 'transparent')};
  height: 27px;
  border-radius: 8px;
  padding-left: 10px;
  margin-bottom: 9px;
`